import React from 'react';
import moment from 'moment';
import { SHIPPING_COLUMNS } from './shippingConfig';

export const isShippingRowShipped = (row) => String(row.Shipped ?? '').trim().toLowerCase() === 'yes' || Boolean(row.ActualShipDate) || String(row.ProjectStatus ?? '').toLowerCase() === 'shipped';
export const SHIPPING_DISPLAY_RANGES = [
  ...Array.from({ length: 6 }, (_, index) => ({ value: String(index + 1), label: `${index + 1} ${index === 0 ? 'day' : 'days'}` })),
  { value: '1w', label: '1 week' },
  { value: '2w', label: '2 weeks' },
  { value: '3w', label: '3 weeks' },
  { value: '1m', label: '1 month' },
];
export const shippingDisplayDays = (today, range = '3') => {
  const days = [];
  const date = moment(today).startOf('day');
  if (!date.isValid()) return days;
  const selected = SHIPPING_DISPLAY_RANGES.some((option) => option.value === String(range)) ? String(range) : '3';
  const end = selected === '1m' ? date.clone().add(1, 'month') : selected.endsWith('w') ? date.clone().add(Number(selected[0]), 'weeks') : null;
  while (end ? date.isBefore(end, 'day') : days.length < Number(selected)) {
    // Today always appears, even on Sunday. Future Sundays are skipped.
    if (days.length === 0 || date.isoWeekday() !== 7) days.push(date.format('YYYY-MM-DD'));
    date.add(1, 'day');
  }
  return days;
};
export const shippingDisplayGroups = (rows, today, range = '3') => {
  const dateOf = (row) => (String(row.ActualShipDate ?? '').trim() || String(row.CurrentPlanShipDate ?? '').trim()).slice(0, 10);
  // Workflow flags do not confirm shipment; only the schedule's ActualShipDate does.
  const pastRows = rows.filter((row) => moment(dateOf(row), 'YYYY-MM-DD', true).isValid() && dateOf(row) < today && String(row.ActualShipDate ?? '').trim() === '');
  const pastDays = [...new Set(pastRows.map(dateOf))].sort();
  return [
    ...pastDays.map((day) => {
      const dailyRows = pastRows.filter((row) => dateOf(row) === day);
      return { day, overdue: true, rows: dailyRows };
    }),
    ...shippingDisplayDays(today, range).map((day) => ({ day, overdue: false, rows: rows.filter((row) => dateOf(row) === day) })),
  ];
};
export const shippingStatusClass = (value) => {
  const status = String(value ?? '').trim().toLowerCase();
  if (['yes', 'complete'].includes(status)) return 'shipping-status-complete';
  if (['no', 'not started'].includes(status)) return 'shipping-status-pending';
  if (['in progress', 'in process', 'pending'].includes(status)) return 'shipping-status-progress';
  if (status === 'n/a') return 'shipping-status-na';
  return '';
};

export default function ShippingDisplay({ rows, today, range = '3', formatValue, renderCell }) {
  const columns = SHIPPING_COLUMNS.display;
  return <div className="shipping-tv-table-wrap">
    <table className="shipping-tv-table" aria-label="Shipping display schedule">
      <colgroup>{[8, 17, 13, 7, 9, 9, 9, 7, 7, 7, 7].map((width, index) => <col key={index} style={{ width: `${width}%` }} />)}</colgroup>
      <thead><tr>{columns.map(({ key, label }) => <th key={key} scope="col">{label}</th>)}</tr></thead>
      {shippingDisplayGroups(rows, today, range).map(({ day, overdue, rows: dailyRows }) => {
        return <tbody key={day}>
          <tr className={`shipping-day-heading ${overdue ? 'shipping-day-overdue' : ''}`}><th scope="rowgroup" colSpan={columns.length}>
            <span>{moment(day).format('dddd')}</span><span className="shipping-day-date">{moment(day).format('MMMM D, YYYY')}</span>
            {overdue && <span className="shipping-day-today">Overdue · Not shipped</span>}
            {day === today && <span className="shipping-day-today">Today</span>}
          </th></tr>
          {dailyRows.length ? dailyRows.map((row) => <tr key={row.ProjectId} className={String(row.JobStatus ?? '').toLowerCase().includes('hold') ? 'shipping-row-hold' : undefined}>
            {columns.map((column) => {
              const { key, type } = column;
              const className = `${['CheckedIn', 'Packed', 'Audit', 'Shipped'].includes(key) ? shippingStatusClass(row[key]) : ''} shipping-tv-${key}`;
              return renderCell ? renderCell(row, column, className) : <td key={key} className={className}>{formatValue(row[key], type)}</td>;
            })}
          </tr>) : <tr><td colSpan={columns.length} className="shipping-day-empty">No shipments scheduled</td></tr>}
        </tbody>;
      })}
    </table>
  </div>;
}
